/**
 * ICP-shaped grouping of merchant category codes into curated segments and tiers.
 *
 * MCC describes *trade*, not modality, so a segment alone does not establish ICP
 * fit — it is one axis alongside solo-ness and phone-vs-terminal usage. The tier
 * is the coarse ICP signal: tiers 1 and 2 are the target market, tier 3 is
 * adjacent, tier 4 is off-ICP and its growth is a warning rather than a win.
 *
 * Mapping is keyed on the numeric code. The repo carries two MCC lists whose
 * codes agree but whose labels differ, so grouping by label would split a
 * segment according to which onboarding version a merchant signed up through.
 *
 * `segment` and `tier` are derived values, never persisted as source of truth:
 * a re-map is a rebuild of whatever consumes this module, not a data migration.
 */

/** ICP proximity of a segment. Lower is closer to the target market. */
export type MccTier = 1 | 2 | 3 | 4;

/** Tier as reported, including the bucket for codes that carry no trade signal. */
export type MccTierValue = MccTier | "unclassified";

export type MccSegmentId =
  | "home_property"
  | "automotive_services"
  | "personal_care"
  | "transport_delivery"
  | "creative_media"
  | "professional_admin"
  | "repair_tech"
  | "health_fitness"
  | "tutoring_education"
  | "pet_services"
  | "mobile_food_drink"
  | "popup_retail_fashion"
  | "market_craft"
  | "fixed_hospitality"
  | "fixed_retail_grocery"
  | "events_entertainment"
  | "digital_goods_software"
  | "telecom_utilities"
  | "travel_rental"
  | "automotive_sales"
  | "wholesale_b2b"
  | "financial_high_risk"
  | "non_profit_membership"
  | "healthcare_practices"
  | "unclassified";

export interface MccSegmentDefinition {
  readonly id: MccSegmentId;
  readonly label: string;
  readonly tier: MccTierValue;
  /** Four-digit MCCs mapped to this segment. Empty for `unclassified`. */
  readonly codes: readonly string[];
}

export const MCC_TIER_LABELS: Record<MccTierValue, string> = {
  1: "Service Pros",
  2: "Mobile Vendors",
  3: "Adjacent",
  4: "Off-ICP",
  unclassified: "Unclassified",
};

/**
 * Codes that are catch-alls by definition and carry no trade information.
 * Together these are the largest share of the production base; they resolve to
 * `unclassified` deliberately, and no mapping work will ever improve them.
 */
export const GENERIC_MCC_CODES: readonly string[] = ["5999", "7299"];

/** Segment returned for generic codes and for any code outside the taxonomy. */
export const UNCLASSIFIED_SEGMENT: MccSegmentDefinition = {
  id: "unclassified",
  label: "Unclassified",
  tier: "unclassified",
  codes: [],
};

export const MCC_SEGMENTS: readonly MccSegmentDefinition[] = [
  {
    id: "home_property",
    label: "Home & Property Services",
    tier: 1,
    codes: [
      "0780",
      "1520",
      "1711",
      "1731",
      "1740",
      "1750",
      "1761",
      "1771",
      "1799",
      "2842",
      "5039",
      "5983",
      "5996",
      "7210",
      "7211",
      "7216",
      "7217",
      "7342",
      "7349",
    ],
  },
  {
    id: "automotive_services",
    label: "Automotive Services",
    tier: 1,
    codes: [
      "5531",
      "5532",
      "5533",
      "5552",
      "5935",
      "7531",
      "7534",
      "7535",
      "7538",
      "7542",
      "7549",
    ],
  },
  {
    id: "personal_care",
    label: "Personal Care & Beauty",
    tier: 1,
    codes: ["5977", "7230", "7296", "7297", "7298"],
  },
  {
    id: "transport_delivery",
    label: "Transport & Delivery",
    tier: 1,
    codes: [
      "4011",
      "4111",
      "4112",
      "4121",
      "4131",
      "4214",
      "4215",
      "4225",
      "4784",
      "4789",
    ],
  },
  {
    id: "creative_media",
    label: "Creative & Media",
    tier: 1,
    codes: [
      "2741",
      "2791",
      "7221",
      "7311",
      "7333",
      "7338",
      "7395",
      "7922",
      "7929",
    ],
  },
  {
    id: "professional_admin",
    label: "Professional & Admin",
    tier: 1,
    codes: [
      "7261",
      "7276",
      "7321",
      "7339",
      "7361",
      "7392",
      "7393",
      "7399",
      "8111",
      "8734",
      "8911",
      "8931",
      "8999",
    ],
  },
  {
    id: "repair_tech",
    label: "Repair & Tech Services",
    tier: 1,
    codes: [
      "7251",
      "7372",
      "7375",
      "7379",
      "7622",
      "7623",
      "7629",
      "7631",
      "7641",
      "7692",
      "7699",
    ],
  },
  {
    id: "health_fitness",
    label: "Health & Fitness",
    tier: 1,
    codes: ["7911", "7997"],
  },
  {
    id: "tutoring_education",
    label: "Tutoring & Education",
    tier: 1,
    codes: ["8211", "8220", "8241", "8244", "8249", "8299", "8351"],
  },
  {
    id: "pet_services",
    label: "Pet Services",
    tier: 1,
    codes: ["0742", "5995"],
  },
  {
    id: "mobile_food_drink",
    label: "Mobile Food & Drink",
    tier: 2,
    codes: ["5451", "5462", "5811", "5814"],
  },
  {
    id: "popup_retail_fashion",
    label: "Pop-up Retail & Fashion",
    tier: 2,
    codes: [
      "5137",
      "5139",
      "5611",
      "5621",
      "5631",
      "5641",
      "5651",
      "5655",
      "5661",
      "5681",
      "5691",
      "5697",
      "5698",
      "5699",
      "5948",
      "5963",
    ],
  },
  {
    id: "market_craft",
    label: "Market & Craft Sellers",
    tier: 2,
    codes: ["5944", "5945", "5947", "5949", "5970", "5971", "5972", "5992"],
  },
  {
    id: "fixed_hospitality",
    label: "Fixed Hospitality",
    tier: 3,
    codes: ["5812", "5813", "5921", "5993", "7011", "7012"],
  },
  {
    id: "fixed_retail_grocery",
    label: "Fixed Retail & Grocery",
    tier: 3,
    codes: [
      "5200",
      "5211",
      "5231",
      "5251",
      "5261",
      "5300",
      "5309",
      "5310",
      "5311",
      "5331",
      "5399",
      "5411",
      "5422",
      "5441",
      "5499",
      "5712",
      "5713",
      "5714",
      "5718",
      "5719",
      "5722",
      "5733",
      "5735",
      "5912",
      "5931",
      "5932",
      "5937",
      "5940",
      "5941",
      "5942",
      "5943",
      "5946",
      "5950",
      "5973",
      "5978",
      "5994",
      "5997",
      "5998",
    ],
  },
  {
    id: "events_entertainment",
    label: "Events & Entertainment",
    tier: 3,
    codes: [
      "7032",
      "7033",
      "7829",
      "7832",
      "7841",
      "7932",
      "7933",
      "7941",
      "7991",
      "7992",
      "7993",
      "7994",
      "7996",
      "7998",
      "7999",
    ],
  },
  {
    id: "digital_goods_software",
    label: "Digital Goods & Software",
    tier: 4,
    codes: ["5045", "5732", "5734", "5815", "5816", "5817", "5818"],
  },
  {
    id: "telecom_utilities",
    label: "Telecom & Utilities",
    tier: 4,
    codes: ["4812", "4814", "4815", "4816", "4821", "4899", "4900"],
  },
  {
    id: "travel_rental",
    label: "Travel & Rental",
    tier: 4,
    codes: [
      "4411",
      "4457",
      "4468",
      "4511",
      "4582",
      "4722",
      "4723",
      "6513",
      "7394",
      "7512",
      "7513",
      "7519",
      "7523",
    ],
  },
  {
    id: "automotive_sales",
    label: "Automotive Sales",
    tier: 4,
    codes: [
      "5271",
      "5511",
      "5521",
      "5541",
      "5542",
      "5551",
      "5561",
      "5571",
      "5592",
      "5598",
      "5599",
    ],
  },
  {
    id: "wholesale_b2b",
    label: "Wholesale & B2B",
    tier: 4,
    codes: [
      "0763",
      "5013",
      "5021",
      "5044",
      "5046",
      "5047",
      "5051",
      "5065",
      "5072",
      "5074",
      "5085",
      "5094",
      "5099",
      "5111",
      "5122",
      "5131",
      "5169",
      "5172",
      "5192",
      "5193",
      "5198",
      "5199",
      "9950",
    ],
  },
  {
    id: "financial_high_risk",
    label: "Financial & High-Risk",
    tier: 4,
    codes: [
      "4829",
      "5933",
      "5960",
      "5962",
      "5964",
      "5965",
      "5966",
      "5967",
      "5968",
      "5969",
      "6010",
      "6011",
      "6012",
      "6051",
      "6211",
      "6300",
      "6399",
      "6540",
      "7273",
      "7277",
      "7278",
      "7800",
      "7801",
      "7802",
      "7995",
      "9223",
      "9406",
    ],
  },
  {
    id: "non_profit_membership",
    label: "Non-Profit & Membership",
    tier: 4,
    codes: [
      "8398",
      "8641",
      "8651",
      "8661",
      "8675",
      "8699",
      "9211",
      "9222",
      "9311",
      "9399",
      "9402",
      "9405",
      "9702",
    ],
  },
  {
    id: "healthcare_practices",
    label: "Healthcare Practices",
    tier: 4,
    codes: [
      "4119",
      "5975",
      "5976",
      "8011",
      "8021",
      "8031",
      "8041",
      "8042",
      "8043",
      "8049",
      "8050",
      "8062",
      "8071",
      "8099",
    ],
  },
  UNCLASSIFIED_SEGMENT,
];

// Every lookup hands back a shared definition, so the readonly contract is
// enforced at runtime too rather than only by the compiler.
for (const segment of MCC_SEGMENTS) {
  Object.freeze(segment.codes);
  Object.freeze(segment);
}
Object.freeze(MCC_SEGMENTS);

const SEGMENT_BY_CODE: ReadonlyMap<string, MccSegmentDefinition> = new Map(
  MCC_SEGMENTS.flatMap((segment) =>
    segment.codes.map((code) => [code, segment] as const),
  ),
);

const SEGMENT_BY_ID: ReadonlyMap<MccSegmentId, MccSegmentDefinition> = new Map(
  MCC_SEGMENTS.map((segment) => [segment.id, segment] as const),
);

/**
 * Resolves an MCC to its curated segment. Never throws and never returns
 * undefined: generic codes, unmapped codes, and missing values all resolve to
 * `unclassified` so that group-bys over a merchant base always total 100%.
 */
export function getMccSegment(
  code: string | null | undefined,
): MccSegmentDefinition {
  if (!code) return UNCLASSIFIED_SEGMENT;
  return SEGMENT_BY_CODE.get(code.trim()) ?? UNCLASSIFIED_SEGMENT;
}

export function getMccSegmentById(
  id: MccSegmentId,
): MccSegmentDefinition | undefined {
  return SEGMENT_BY_ID.get(id);
}

/**
 * True when the code is explicitly placed in the taxonomy. Distinguishes a
 * merchant who declared a catch-all code from one whose code the taxonomy does
 * not yet cover — the latter is a signal to extend the mapping, so callers
 * should count these separately rather than folding them into `unclassified`.
 */
export function isMappedMcc(code: string | null | undefined): boolean {
  if (!code) return false;
  return SEGMENT_BY_CODE.has(code.trim());
}

/** True for catch-all codes that carry no trade information by definition. */
export function isGenericMcc(code: string | null | undefined): boolean {
  if (!code) return false;
  return GENERIC_MCC_CODES.includes(code.trim());
}
